import Papa from 'papaparse';

export const CSV_DELIMITERS = Object.freeze([
  { value: 'auto', labelKey: 'search.synonyms.import.delimiter.auto' },
  { value: ',', labelKey: 'search.synonyms.import.delimiter.comma' },
  { value: ';', labelKey: 'search.synonyms.import.delimiter.semicolon' },
  { value: '\t', labelKey: 'search.synonyms.import.delimiter.tab' },
  { value: '|', labelKey: 'search.synonyms.import.delimiter.pipe' },
]);

export const GROUP_SEPARATORS = Object.freeze([',', ';', '|']);
export const MAX_IMPORT_FILE_BYTES = 2 * 1024 * 1024;
export const MAX_IMPORT_SOURCE_ROWS = 10000;
export const MAX_IMPORT_RELATIONS = 20000;

const normalizeDisplayTerm = value => String(value == null ? '' : value)
  .trim()
  .replace(/\s+/g, ' ');

export const normalizeImportTerm = value => normalizeDisplayTerm(value).toLowerCase();

const canonicalTerms = values => Array.from(new Set(
  (Array.isArray(values) ? values : [values]).map(normalizeImportTerm).filter(Boolean),
)).sort();

const unorderedRelationKey = (left, right) => (
  [normalizeImportTerm(left), normalizeImportTerm(right)].sort().join('\u0000')
);

const entryKey = entry => {
  const left = canonicalTerms(entry.left);
  const right = canonicalTerms(entry.right);
  if (entry.bidirectional) {
    const pairs = left.flatMap(leftTerm => (
      right.map(rightTerm => unorderedRelationKey(leftTerm, rightTerm))
    ));
    return `both:${Array.from(new Set(pairs)).sort().join('\u0001')}`;
  }
  return `one:${left.join('\u0001')}\u0000${right.join('\u0001')}`;
};

const sourceKey = entry => canonicalTerms(entry.left).join('\u0001');

const makeIssue = (line, code, values = {}) => ({ line, code, values });

const invalidTermCode = term => {
  if (Array.from(term).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  })) return 'control_character';
  if (Array.from(term).length > 255) return 'term_too_long';
  return null;
};

export function parseSynonymCsv(text, delimiter = 'auto') {
  const source = String(text || '').replace(/^\uFEFF/, '');
  const result = Papa.parse(source, {
    delimiter: delimiter === 'auto' ? '' : delimiter,
    skipEmptyLines: 'greedy',
  });
  const rows = (result.data || []).map(row => (
    (Array.isArray(row) ? row : [row]).map(value => String(value == null ? '' : value))
  ));
  const errors = (result.errors || [])
    .filter(error => error.code !== 'UndetectableDelimiter')
    .map(error => makeIssue((error.row || 0) + 1, 'csv_parse_error', {
      message: error.message,
    }));

  return {
    rows,
    errors,
    delimiter: result.meta?.delimiter || (delimiter === 'auto' ? ',' : delimiter),
  };
}

export function getImportColumns(rows, hasHeader, fallbackLabel = index => `Column ${index + 1}`) {
  const width = Math.max(0, ...(rows || []).slice(0, 20).map(row => row.length));
  const header = hasHeader ? (rows[0] || []) : [];
  return Array.from({ length: width }, (_, index) => ({
    value: index,
    label: normalizeDisplayTerm(header[index]) || fallbackLabel(index),
    sample: normalizeDisplayTerm((rows[hasHeader ? 1 : 0] || [])[index]),
  }));
}

const uniqueTerms = values => {
  const seen = new Set();
  return values.reduce((terms, value) => {
    const display = normalizeDisplayTerm(value);
    const normalized = normalizeImportTerm(display);
    if (display && !seen.has(normalized)) {
      seen.add(normalized);
      terms.push(display);
    }
    return terms;
  }, []);
};

function createRelation(left, right, bidirectional, enabled, line) {
  return {
    left: (Array.isArray(left) ? left : [left]).map(normalizeDisplayTerm),
    right: (Array.isArray(right) ? right : [right]).map(normalizeDisplayTerm),
    bidirectional: Boolean(bidirectional),
    enabled,
    source: 'csv-import',
    sourceLine: line,
    sourceMatchAllowed: true,
  };
}

export function buildImportedRelations({
  rows,
  hasHeader = true,
  shape = 'pair',
  sourceColumn = 0,
  targetColumn = 1,
  groupColumn = 0,
  groupSeparator = ',',
  bidirectional = false,
}) {
  const sourceRows = (rows || []).slice(hasHeader ? 1 : 0);
  const dataRows = sourceRows.slice(0, MAX_IMPORT_SOURCE_ROWS);
  const relations = [];
  const issues = [];
  const seenRelations = new Map();
  const directionIndex = new Map();
  let duplicateCount = 0;
  let conflictCount = 0;
  const header = hasHeader ? (rows[0] || []).map(normalizeImportTerm) : [];
  const directionColumn = header.findIndex(value => value === 'direction');
  const statusColumn = header.findIndex(value => value === 'status');

  if (sourceRows.length > MAX_IMPORT_SOURCE_ROWS) {
    issues.push(makeIssue(0, 'too_many_rows', { limit: MAX_IMPORT_SOURCE_ROWS }));
  }

  const addRelation = (left, right, relationBidirectional, enabled, line) => {
    const leftValues = Array.isArray(left) ? left : [left];
    const rightValues = Array.isArray(right) ? right : [right];
    const normalizedLeft = leftValues.map(normalizeImportTerm).filter(Boolean);
    const normalizedRight = rightValues.map(normalizeImportTerm).filter(Boolean);
    if (!normalizedLeft.length || !normalizedRight.length) {
      issues.push(makeIssue(line, 'missing_term'));
      return;
    }
    const invalidCode = [...leftValues, ...rightValues]
      .map(normalizeDisplayTerm)
      .map(invalidTermCode)
      .find(Boolean);
    if (invalidCode) {
      issues.push(makeIssue(line, invalidCode, { limit: 255 }));
      return;
    }
    if (normalizedLeft.some(term => normalizedRight.includes(term))) {
      issues.push(makeIssue(line, 'same_term'));
      return;
    }

    const relation = createRelation(left, right, relationBidirectional, enabled, line);
    relation.sourceMatchAllowed = shape === 'pair';
    const key = entryKey(relation);
    if (seenRelations.has(key)) {
      duplicateCount += 1;
      issues.push(makeIssue(line, 'duplicate_in_file', {
        firstLine: seenRelations.get(key),
      }));
      return;
    }
    const relationPairs = new Set(relation.left.flatMap(leftTerm => (
      relation.right.map(rightTerm => unorderedRelationKey(leftTerm, rightTerm))
    )));
    const conflicting = Array.from(relationPairs)
      .map(pair => directionIndex.get(pair))
      .find(accepted => accepted && (relation.bidirectional || accepted.bidirectional));
    if (conflicting) {
      conflictCount += 1;
      issues.push(makeIssue(line, 'direction_conflict_in_file', {
        firstLine: conflicting.line,
      }));
      return;
    }
    if (relations.length >= MAX_IMPORT_RELATIONS) {
      issues.push(makeIssue(line, 'too_many_relations', { limit: MAX_IMPORT_RELATIONS }));
      return;
    }

    seenRelations.set(key, line);
    relationPairs.forEach((pair) => {
      if (!directionIndex.has(pair)) {
        directionIndex.set(pair, { bidirectional: relation.bidirectional, line });
      }
    });
    relations.push(relation);
  };

  dataRows.forEach((row, dataIndex) => {
    const line = dataIndex + (hasHeader ? 2 : 1);
    const rowDirection = directionColumn >= 0
      ? normalizeImportTerm(row[directionColumn])
      : '';
    const rowStatus = statusColumn >= 0
      ? normalizeImportTerm(row[statusColumn])
      : '';
    const relationBidirectional = rowDirection
      ? rowDirection === 'bidirectional'
      : bidirectional;
    const relationEnabled = rowStatus ? rowStatus !== 'disabled' : true;
    if (shape === 'group') {
      const terms = uniqueTerms(String(row[groupColumn] || '').split(groupSeparator));
      if (terms.length < 2) {
        issues.push(makeIssue(line, 'group_needs_two_terms'));
        return;
      }
      const relationCount = relationBidirectional
        ? (terms.length * (terms.length - 1)) / 2
        : terms.length - 1;
      if (relations.length + relationCount > MAX_IMPORT_RELATIONS) {
        issues.push(makeIssue(line, 'too_many_relations', { limit: MAX_IMPORT_RELATIONS }));
        return;
      }
      if (relationBidirectional) {
        for (let leftIndex = 0; leftIndex < terms.length - 1; leftIndex += 1) {
          for (let rightIndex = leftIndex + 1; rightIndex < terms.length; rightIndex += 1) {
            addRelation(terms[leftIndex], terms[rightIndex], true, relationEnabled, line);
          }
        }
      } else {
        terms.slice(1).forEach(term => addRelation(
          terms[0], term, false, relationEnabled, line,
        ));
      }
      return;
    }

    const source = normalizeDisplayTerm(row[sourceColumn]);
    const target = normalizeDisplayTerm(row[targetColumn]);
    if (!source || !target) {
      issues.push(makeIssue(line, !source && !target ? 'empty_row' : 'missing_term'));
      return;
    }
    addRelation(source, target, relationBidirectional, relationEnabled, line);
  });

  return {
    relations,
    issues,
    stats: {
      sourceRows: sourceRows.length,
      validRelations: relations.length,
      invalidRows: new Set(issues
        .filter(issue => !['duplicate_in_file', 'direction_conflict_in_file'].includes(issue.code))
        .map(issue => issue.line)).size,
      duplicates: duplicateCount,
      conflicts: conflictCount,
    },
  };
}

const expandExisting = synonym => {
  const left = Array.isArray(synonym.left) ? synonym.left : [synonym.left];
  const right = Array.isArray(synonym.right) ? synonym.right : [synonym.right];
  const relations = [];
  left.forEach(leftTerm => right.forEach(rightTerm => {
    if (normalizeImportTerm(leftTerm) && normalizeImportTerm(rightTerm)) {
      relations.push({
        orderedKey: `${normalizeImportTerm(leftTerm)}\u0000${normalizeImportTerm(rightTerm)}`,
        unorderedKey: unorderedRelationKey(leftTerm, rightTerm),
        left: leftTerm,
        right: rightTerm,
      });
    }
  }));
  return relations;
};

const hasDirectionConflict = (existing, incoming) => {
  if (!existing.bidirectional && !incoming.bidirectional) return false;
  const incomingPairs = new Set(expandExisting(incoming).map(item => item.unorderedKey));
  return expandExisting(existing).some(item => incomingPairs.has(item.unorderedKey));
};

const hasSamePairDirection = (existing, incoming) => {
  if (Boolean(existing.bidirectional) !== Boolean(incoming.bidirectional)) return false;
  const existingPairs = new Set(expandExisting(existing).map(pair => (
    existing.bidirectional ? pair.unorderedKey : pair.orderedKey
  )));
  return expandExisting(incoming).every(pair => existingPairs.has(
    incoming.bidirectional ? pair.unorderedKey : pair.orderedKey,
  ));
};

const importedRow = (relation, id) => ({
  _id: id,
  left: relation.left,
  right: relation.right,
  bidirectional: relation.bidirectional,
  enabled: relation.enabled !== false,
  source: 'csv-import',
  _isNew: true,
});

export function planSynonymImport(existingSynonyms, importedRelations, mode = 'add', options = {}) {
  const existing = (existingSynonyms || []).map(synonym => ({ ...synonym }));
  const incoming = importedRelations || [];
  const idPrefix = options.idPrefix || `temp-import-${Date.now()}`;
  const makeId = index => `${idPrefix}-${index}`;

  if (mode === 'replace') {
    return {
      synonyms: incoming.map((relation, index) => importedRow(relation, makeId(index))),
      issues: [],
      stats: {
        added: incoming.length,
        updated: 0,
        duplicates: 0,
        conflicts: 0,
        skipped: 0,
        removed: existing.length,
      },
    };
  }

  const next = [...existing];
  const importedIndexes = [];
  const issues = [];
  const stats = {
    added: 0,
    updated: 0,
    duplicates: 0,
    conflicts: 0,
    skipped: 0,
    removed: 0,
  };

  const addToIndex = (map, key, index) => {
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(index);
  };
  const removeFromIndex = (map, key, index) => {
    const indexes = map.get(key);
    if (!indexes) return;
    indexes.delete(index);
    if (!indexes.size) map.delete(key);
  };
  const exactIndex = new Map();
  const sourceIndex = new Map();
  const pairIndex = new Map();
  const incomingSourceCounts = incoming.reduce((counts, relation) => {
    const key = sourceKey(relation);
    counts.set(key, (counts.get(key) || 0) + 1);
    return counts;
  }, new Map());
  const indexSynonym = (synonym, index) => {
    addToIndex(exactIndex, entryKey(synonym), index);
    addToIndex(sourceIndex, sourceKey(synonym), index);
    expandExisting(synonym).forEach(pair => addToIndex(pairIndex, pair.unorderedKey, index));
  };
  const unindexSynonym = (synonym, index) => {
    removeFromIndex(exactIndex, entryKey(synonym), index);
    removeFromIndex(sourceIndex, sourceKey(synonym), index);
    expandExisting(synonym).forEach(pair => removeFromIndex(pairIndex, pair.unorderedKey, index));
  };
  next.forEach(indexSynonym);

  incoming.forEach((relation, importIndex) => {
    const key = entryKey(relation);
    const exactMatches = exactIndex.get(key);
    const exactMatchIndex = exactMatches ? exactMatches.values().next().value : -1;

    if (exactMatchIndex >= 0) {
      const match = next[exactMatchIndex];
      const displayChanged = JSON.stringify(match.left) !== JSON.stringify(relation.left)
        || JSON.stringify(match.right) !== JSON.stringify(relation.right);
      const statusChanged = (match.enabled !== false) !== (relation.enabled !== false);
      if (mode === 'update' && (displayChanged || statusChanged)) {
        unindexSynonym(match, exactMatchIndex);
        next[exactMatchIndex] = {
          ...match,
          left: relation.left,
          right: relation.right,
          bidirectional: relation.bidirectional,
          enabled: relation.enabled !== false,
          source: match.source || 'csv-import',
          _isModified: !match._isNew || match._isModified,
        };
        indexSynonym(next[exactMatchIndex], exactMatchIndex);
        stats.updated += 1;
      } else {
        stats.duplicates += 1;
        stats.skipped += 1;
        issues.push(makeIssue(relation.sourceLine, 'duplicate_existing'));
      }
      return;
    }

    const samePairCandidates = new Set();
    expandExisting(relation).forEach(pair => {
      (pairIndex.get(pair.unorderedKey) || []).forEach(index => samePairCandidates.add(index));
    });
    const representedPairIndex = Array.from(samePairCandidates)
      .find(index => hasSamePairDirection(next[index], relation));
    if (representedPairIndex !== undefined) {
      stats.duplicates += 1;
      stats.skipped += 1;
      issues.push(makeIssue(relation.sourceLine, 'duplicate_existing'));
      return;
    }

    if (mode === 'update' && samePairCandidates.size === 1) {
      const pairMatchIndex = samePairCandidates.values().next().value;
      const pairMatch = next[pairMatchIndex];
      if (hasDirectionConflict(pairMatch, relation)
        && expandExisting(pairMatch).length === 1
        && expandExisting(relation).length === 1) {
        unindexSynonym(pairMatch, pairMatchIndex);
        next[pairMatchIndex] = {
          ...pairMatch,
          left: relation.left,
          right: relation.right,
          bidirectional: relation.bidirectional,
          enabled: relation.enabled !== false,
          source: pairMatch.source || 'csv-import',
          _isModified: !pairMatch._isNew || pairMatch._isModified,
        };
        indexSynonym(next[pairMatchIndex], pairMatchIndex);
        stats.updated += 1;
        return;
      }
    }

    const conflictIndex = Array.from(samePairCandidates)
      .find(index => hasDirectionConflict(next[index], relation));
    if (conflictIndex !== undefined) {
      stats.conflicts += 1;
      stats.skipped += 1;
      issues.push(makeIssue(relation.sourceLine, 'direction_conflict_existing'));
      return;
    }

    if (mode === 'update') {

      const relationSourceKey = sourceKey(relation);
      const matchingSourceIndexes = relation.sourceMatchAllowed !== false
        && incomingSourceCounts.get(relationSourceKey) === 1
        ? Array.from(sourceIndex.get(relationSourceKey) || [])
        : [];
      if (matchingSourceIndexes.length === 1) {
        const sourceMatchIndex = matchingSourceIndexes[0];
        const sourceMatch = next[sourceMatchIndex];
        if (expandExisting(sourceMatch).length !== 1) {
          stats.conflicts += 1;
          stats.skipped += 1;
          issues.push(makeIssue(relation.sourceLine, 'ambiguous_source_existing'));
          return;
        }
        unindexSynonym(sourceMatch, sourceMatchIndex);
        next[sourceMatchIndex] = {
          ...sourceMatch,
          left: relation.left,
          right: relation.right,
          bidirectional: relation.bidirectional,
          enabled: relation.enabled !== false,
          source: sourceMatch.source || 'csv-import',
          _isModified: !sourceMatch._isNew || sourceMatch._isModified,
        };
        indexSynonym(next[sourceMatchIndex], sourceMatchIndex);
        stats.updated += 1;
        return;
      }
      if (matchingSourceIndexes.length > 1) {
        stats.conflicts += 1;
        stats.skipped += 1;
        issues.push(makeIssue(relation.sourceLine, 'ambiguous_source_existing'));
        return;
      }
    }

    const newIndex = next.length;
    next.push(importedRow(relation, makeId(importIndex)));
    importedIndexes.push(newIndex);
    indexSynonym(next[newIndex], newIndex);
    stats.added += 1;
  });

  const importedIndexSet = new Set(importedIndexes);
  return {
    synonyms: [
      ...importedIndexes.map(index => next[index]),
      ...next.filter((synonym, index) => !importedIndexSet.has(index)),
    ],
    issues,
    stats,
  };
}

export function inferImportShape(rows, hasHeader = true) {
  const columns = getImportColumns(rows, hasHeader);
  return columns.length <= 1 ? 'group' : 'pair';
}
